import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import { GraduateFormPayload, GraduateFormValues, Props } from '@/forms/GraduateForm/types';
import useGraduationReasons from '@/hooks/useGraduationReasons';
import EpisodeOfCareRepository from '@/repositories/EpisodeOfCareRepository/EpisodeOfCareRepository';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import Textarea from '@/ui/components/Form/Textarea';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const GraduateForm = ({ episodeOfCareId, onSuccess, personId }: Props) => {
  const { addMessage, currentUser } = useContext(AppContext);
  const queryClient = useQueryClient();
  const episodeOfCareRepository = EpisodeOfCareRepository();
  const { data: graduationReasons, isPending: isLoadingGraduationReasons } = useGraduationReasons();

  const initialValues: GraduateFormValues = {
    graduation_reason: '',
    discharged_by: currentUser?.id,
    notes: ''
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register
  } = useForm({ defaultValues: initialValues });

  const graduate = useMutation({
    mutationFn: ({ values }: { values: GraduateFormPayload }) => {
      return episodeOfCareRepository.graduate({ params: values, episodeOfCareId });
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully graduated person' });
      personId && (await queryClient.invalidateQueries({ queryKey: ['getPerson', personId] }));
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: GraduateFormValues) => {
    const payload: GraduateFormPayload = {
      ...values,
      notes: values.notes ?? ''
    };
    graduate.mutate({ values: payload });
  };

  return (
    <div>
      <Heading value={3} text="Please select the graduation reason" />

      <form onSubmit={handleSubmit(submitForm)} data-testid="graduate-form">
        <InputGroup>
          {isLoadingGraduationReasons ? (
            <Spinner label="Loading graduation reasons" />
          ) : graduationReasons ? (
            <>
              <Label htmlFor="" text="Graduation Reason">
                {Object.values(graduationReasons).map((graduationReason) => {
                  const id = `graduation-reason-${graduationReason.value}`;

                  return (
                    <div key={id}>
                      <Label htmlFor={id} reverse text={graduationReason.name}>
                        <input
                          {...register('graduation_reason', {
                            required: 'Graduation reason is required'
                          })}
                          className="mr-1 mb-1"
                          id={id}
                          type="radio"
                          value={graduationReason.name}
                        />
                      </Label>
                    </div>
                  );
                })}
              </Label>
            </>
          ) : null}

          <FieldError message={errors.graduation_reason?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => {
              return (
                <Textarea
                  id="id-notes"
                  labelText="Notes"
                  name={field.name}
                  onChange={field.onChange}
                  placeholder="Type any additional info for this graduation"
                />
              );
            }}
          />

          <FieldError message={errors.notes?.message} />
        </InputGroup>

        <InputGroup>
          <Button disabled={graduate.isPending} type="submit">
            {graduate.isPending ? <Spinner color={Color.WHITE} label="Submitting..." /> : 'Submit'}
          </Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default GraduateForm;
